import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { get } from "common/helpers/HTTP";

import { useCampaignContext } from "../context/CampaignContext";

import type { LinkedInGroup } from "../types";

async function getUserGroupsRequest(
  workspaceId: string,
  campaignId: string,
): Promise<LinkedInGroup[]> {
  return (await get(`workspaces/${workspaceId}/campaigns/${campaignId}/groups`))
    .groups;
}

async function getLinkedInGroupRequest(
  workspaceId: string,
  campaignId: string,
  groupId: string,
): Promise<LinkedInGroup> {
  return (
    await get(
      `workspaces/${workspaceId}/campaigns/${campaignId}/groups/${groupId}`,
    )
  ).group;
}

export default function useLinkedInGroups({
  groupId,
  disableQuery,
}: {
  groupId?: string;
  disableQuery?: boolean;
} = {}) {
  const { id: campaignId, miner } = useCampaignContext();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { campaignsKeys } = getQueryKeys(workspaceId);

  const {
    data: linkedInGroups,
    isLoading: isLoadingLinkedInGroups,
    refetch: refetchLinkedInGroups,
  } = useQuery({
    queryKey: campaignsKeys.linkedInGroups(campaignId, miner),
    queryFn: () => getUserGroupsRequest(workspaceId, campaignId),
    enabled: !groupId && !disableQuery,
  });

  const { data: linkedInGroup, isLoading: isLoadingLinkedInGroup } = useQuery({
    queryKey: campaignsKeys.linkedInGroup(campaignId, groupId),
    queryFn: () => getLinkedInGroupRequest(workspaceId, campaignId, groupId),
    enabled: !!groupId,
  });

  return {
    linkedInGroups,
    isLoadingLinkedInGroups,
    linkedInGroup,
    isLoadingLinkedInGroup,
    refetchLinkedInGroups,
  };
}
